import { clickedOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/logged-out-page-click';
import {
  getSearchArea,
  getSearchLocationCounty,
} from '@bridebook/web/app-basic/lib/search/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { useRouter } from 'next/router';
import { MouseEventHandler, SyntheticEvent, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getHomeArea } from '@bridebook/toolbox/src/search-suppliers/get-home-area';
import { Properties } from '../supplier-properties';
import { SearchRating } from '../search-rating';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { TLoggedOutPageClickedSection } from '@bridebook/web/app-shared/lib/types';
import { SupplierCardDescription } from '@bridebook/web/components/blocks/search-list-item';
import { CarouselGridGallery } from '../carousel-grid-gallery';
import { FeatureSupplierMarker } from '../feature-supplier-marker';
import {
  LocationFilledSpriteIcon,
  ReviewStarSpriteIcon,
  ShortlistVariantSpriteIcon,
} from '../search-svg-sprite';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { arePropsEqual } from '@bridebook/web/lib/utils';
import { useSearchItemCommon } from '@bridebook/web/components/search/search-desktop/search-list/use-search-item-common';
import { SearchListItemProps } from '@bridebook/web/components/search/search-desktop/search-list/search-list-item-props';
import { cn } from '@bridebook/aura';

const SearchListItem = ({ supplier, itemIndex }: SearchListItemProps) => {
  const market = useMarket();
  const router = useRouter();
  const searchLocation = useSelector(getSearchArea);
  const searchLocationCounty = useSelector(getSearchLocationCounty);
  const filters = useSelector(({ search }) => search.filters).venueType;
  const activeFilters = filters ? Object.keys(filters).filter((k) => filters[k]) : [];

  const {
    photos,
    thumb,
    pills,
    showReviews,
    isTopSpotVenue,
    showFeatureSupplierMarker,
    locationPillValue,
    isVenue,
    supplierUrl,
    isVenueTier_3,
    t,
    showTopSpotLayout,
    showCountryTopSpotLayout,
    searchBoostForType,
    isForeign,
  } = useSearchItemCommon(supplier, {
    searchLocation: searchLocation ?? '',
    searchLocationCounty,
  });

  const tDynamic = t;
  const dispatch = useDispatch();

  const buttonText = isVenue
    ? t('supplier:button.viewWeddingVenue')
    : t('supplier:button.findOutMore');

  const shortlistText = t('common:button.favourites');

  const targetUrl = `/${market.prefix}${supplierUrl}`;

  const loggOutPageClickAnalytics = useCallback(
    (
      cta: string,
      clickedLocation: string = 'search',
      clickedSection: TLoggedOutPageClickedSection = 'supplierTile',
    ) => {
      dispatch(
        clickedOnLoggedOutPage({
          clickedLocation,
          clickedSection,
          cta,
          supplierCountryCode: supplier.countryCode,
          supplierCategory: supplier.type,
        }),
      );
    },
    [dispatch, supplier.countryCode, supplier.type],
  );

  const handleOnCardClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.stopPropagation();
      loggOutPageClickAnalytics('searchProfileImage');
      router.push(targetUrl);
    },
    [loggOutPageClickAnalytics, router, targetUrl],
  );

  const handleInCardLinkClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      loggOutPageClickAnalytics('searchProfileImage');
    },
    [loggOutPageClickAnalytics],
  );

  const handleOnButtonClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      loggOutPageClickAnalytics(buttonText);
    },
    [buttonText, loggOutPageClickAnalytics],
  );

  const toggleShortlisted = useCallback(() => {
    loggOutPageClickAnalytics('shortlistButton', 'searchResults', 'tile');
    router.push(UrlHelper.signup);
  }, [loggOutPageClickAnalytics, router]);

  const handleShortlistClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      e.preventDefault();
      toggleShortlisted();
    },
    [toggleShortlisted],
  );

  const homeArea = getHomeArea(supplier);
  const locationMark =
    !!locationPillValue && locationPillValue === supplier?.town && homeArea
      ? `${supplier.town}, ${homeArea}`
      : locationPillValue;

  const allPhotos = [thumb, ...photos];

  const showGallery = isVenueTier_3 || isTopSpotVenue;

  const showSearchBoostForTypeHeader =
    searchBoostForType && activeFilters.includes(searchBoostForType) && !isForeign;

  const articleClasses = cn('bsb', showGallery && 'bsp', showTopSpotLayout && 'bsq');
  return (
    <article onClick={handleOnCardClick} className={articleClasses}>
      {(showCountryTopSpotLayout || showTopSpotLayout || showSearchBoostForTypeHeader) && (
        <div className={cn('bsw', showSearchBoostForTypeHeader && 'bsx')}>
          <ReviewStarSpriteIcon width={14} height={14} />
          {showCountryTopSpotLayout
            ? t('supplier:msg.countryFeaturedVenue')
            : showTopSpotLayout
              ? t('supplier:msg.featuredVenue')
              : `${tDynamic(`filters:${searchBoostForType}`)} ${t('supplier:hero')}`}
        </div>
      )}

      <a onClick={handleInCardLinkClick} href={targetUrl}>
        <CarouselGridGallery
          photos={allPhotos}
          showGallery={!!showGallery}
          isVenue={isVenue}
          itemIndex={itemIndex}
        />
        {showFeatureSupplierMarker && <FeatureSupplierMarker />}
        <div className="bsj">
          <button aria-label={shortlistText} onClick={handleShortlistClick}>
            <ShortlistVariantSpriteIcon width={26} height={26} />
          </button>
        </div>
      </a>

      <div className="bsz">
        <a onClick={handleInCardLinkClick} href={targetUrl}>
          <h2>{supplier.name}</h2>
        </a>
        <div className="bsc">
          {locationMark && (
            <div className="bsd">
              <LocationFilledSpriteIcon width={12} height={15} />
              <span>{locationMark}</span>
            </div>
          )}
          {showReviews && (
            <SearchRating stars={supplier.reviewsStars} count={supplier.reviewsCount ?? 0} />
          )}
        </div>
        <div className="bse">
          {pills.map(({ label, condition }, index) =>
            condition ? <span key={index}>{label}</span> : null,
          )}
        </div>
        {supplier.description && (
          <div className="bsm">
            <SupplierCardDescription
              linesAmount={isVenueTier_3 || isTopSpotVenue ? 4 : 2}
              description={supplier.description}
            />
          </div>
        )}
        <Properties market={market} supplier={supplier} />
      </div>

      <footer>
        <button onClick={handleShortlistClick}>
          <ShortlistVariantSpriteIcon width={26} height={26} />
          {shortlistText}
        </button>
        <a href={targetUrl} onClick={handleOnButtonClick}>
          {buttonText}
        </a>
      </footer>
    </article>
  );
};

export const SearchListItemLoggedOut = memo(SearchListItem, arePropsEqual);
